import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Image as ImageIcon,
  LayoutGrid,
  Info,
  Mail,
  Users,
  BookMarked,
  CalendarDays,
  List,
  Plus,
  Trash2,
} from 'lucide-react';
import { uploadsAbsoluteUrl } from '../../../../utils/uploadsBase';
import MediaSelectModal from '../MediaSelectModal';
import './HeroHomeEditor.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function merge(comp, changes) {
  const base =
    typeof comp.content === 'string'
      ? JSON.parse(comp.content || '{}')
      : JSON.parse(JSON.stringify(comp.content || {}));
  const ch = changes[comp.id] || {};
  Object.entries(ch).forEach(([pathStr, val]) => {
    if (pathStr === '__mediaId__') return;
    const parts = pathStr.split('.');
    let cur = base;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = parts[i + 1];
      if (cur[p] == null) cur[p] = /^\d+$/.test(String(next)) ? [] : {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = val;
  });
  return base;
}

function useMediaList(token) {
  return useQuery({
    queryKey: ['adminMediaList', token],
    queryFn: async () => {
      const r = await axios.get(`${API}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.data.media || [];
    },
    enabled: !!token,
    staleTime: 60_000,
  });
}

function MediaPickerBlock({ comp, changes, onUpdate, token, title }) {
  const { data: mediaList = [] } = useMediaList(token);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const ch = changes[comp.id] || {};
  const mediaId = Object.prototype.hasOwnProperty.call(ch, '__mediaId__')
    ? ch.__mediaId__
    : comp.mediaId;
  const selected = mediaList.find((m) => m.id === mediaId) || comp.media;
  const preview = selected?.url
    ? uploadsAbsoluteUrl(selected.url, API)
    : null;

  return (
    <section className="hero-home-section hero-home-section--media">
      <h4>
        <ImageIcon size={18} /> {title}
      </h4>
      <div className="hero-media-row">
        {preview ? (
          <div className="hero-media-preview">
            <img src={preview} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            <button
              type="button"
              className="hero-media-clear"
              onClick={() => onUpdate(comp.id, ['__mediaId__'], null)}
            >
              Сбросить
            </button>
          </div>
        ) : (
          <div className="hero-media-empty">Нет изображения</div>
        )}
        <div className="hero-media-select-wrap">
          <label className="hero-media-label">Медиатека</label>
          <button
            type="button"
            className="hero-media-open-modal"
            onClick={() => setMediaModalOpen(true)}
          >
            Открыть галерею…
          </button>
          <p className="hero-media-modal-hint">
            Выбор по превью в модальном окне.
          </p>
        </div>
      </div>
      <MediaSelectModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        token={token}
        apiUrl={API}
        selectedId={mediaId}
        onSelect={(id) => onUpdate(comp.id, ['__mediaId__'], id)}
        title={title}
      />
    </section>
  );
}

function TriInput({ label, codes, values, onChange }) {
  return (
    <section className="hero-home-section">
      <h4>{label}</h4>
      <div className="hero-lang-grid">
        {codes.map((code) => (
          <label key={code} className="hero-lang-field">
            <span className="hero-lang-code">{code.toUpperCase()}</span>
            <input
              type="text"
              value={values[code] ?? ''}
              onChange={(e) => onChange(code, e.target.value)}
            />
          </label>
        ))}
      </div>
    </section>
  );
}

function TriTextarea({ label, codes, values, onChange, rows = 4 }) {
  return (
    <section className="hero-home-section">
      <h4>{label}</h4>
      <div className="hero-lang-grid">
        {codes.map((code) => (
          <label key={code} className="hero-lang-field">
            <span className="hero-lang-code">{code.toUpperCase()}</span>
            <textarea
              rows={rows}
              value={values[code] ?? ''}
              onChange={(e) => onChange(code, e.target.value)}
            />
          </label>
        ))}
      </div>
    </section>
  );
}

export function HomeMainEditor({ component: comp, languages, changes, onUpdate, token }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => merge(comp, changes), [comp, changes]);
  const val = (field) =>
    Object.fromEntries(codes.map((code) => [code, c[code]?.[field] ?? '']));

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <LayoutGrid size={22} />
        <div>
          <h3 className="hero-home-editor__title">Блок «Контент + картинка» (Main)</h3>
          <p className="hero-home-editor__hint">Заголовок, текст, кнопка, изображение справа.</p>
        </div>
      </div>
      <TriInput
        label="Заголовок"
        codes={codes}
        values={val('title')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />
      <TriTextarea
        label="Описание"
        codes={codes}
        values={val('description')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'description'], v)}
      />
      <TriInput
        label="Текст кнопки"
        codes={codes}
        values={val('buttonText')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'buttonText'], v)}
      />
      <TriInput
        label="Ссылка кнопки"
        codes={codes}
        values={val('buttonLink')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'buttonLink'], v)}
      />
      <TriInput
        label="Alt изображения"
        codes={codes}
        values={val('imageAlt')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'imageAlt'], v)}
      />
      <MediaPickerBlock
        comp={comp}
        changes={changes}
        onUpdate={onUpdate}
        token={token}
        title="Изображение (общее)"
      />
    </div>
  );
}

export function HomeAboutEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => merge(comp, changes), [comp, changes]);
  const val = (field) =>
    Object.fromEntries(codes.map((code) => [code, c[code]?.[field] ?? '']));

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <Info size={22} />
        <div>
          <h3 className="hero-home-editor__title">Блок «О нас»</h3>
        </div>
      </div>
      <TriInput
        label="Заголовок"
        codes={codes}
        values={val('title')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />
      <TriTextarea
        label="Текст"
        codes={codes}
        values={val('description')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'description'], v)}
      />
      <TriInput
        label="Текст кнопки"
        codes={codes}
        values={val('buttonText')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'buttonText'], v)}
      />
      <TriInput
        label="Ссылка"
        codes={codes}
        values={val('buttonLink')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'buttonLink'], v)}
      />
    </div>
  );
}

export function HomeInvitationEditor({ component: comp, languages, changes, onUpdate, token }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => merge(comp, changes), [comp, changes]);
  const val = (field) =>
    Object.fromEntries(codes.map((code) => [code, c[code]?.[field] ?? '']));

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <Mail size={22} />
        <div>
          <h3 className="hero-home-editor__title">Блок «Приглашение»</h3>
        </div>
      </div>
      <TriInput
        label="Заголовок"
        codes={codes}
        values={val('title')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />
      <TriInput
        label="Подзаголовок"
        codes={codes}
        values={val('subtitle')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'subtitle'], v)}
      />
      <TriTextarea
        label="Описание"
        codes={codes}
        values={val('description')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'description'], v)}
      />
      <TriInput
        label="Alt изображения"
        codes={codes}
        values={val('imageAlt')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'imageAlt'], v)}
      />
      <MediaPickerBlock
        comp={comp}
        changes={changes}
        onUpdate={onUpdate}
        token={token}
        title="Изображение блока"
      />
    </div>
  );
}

export function HomeIndexingEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => merge(comp, changes), [comp, changes]);
  const val = (field) =>
    Object.fromEntries(codes.map((code) => [code, c[code]?.[field] ?? '']));

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <BookMarked size={22} />
        <div>
          <h3 className="hero-home-editor__title">Индексация</h3>
        </div>
      </div>
      <TriInput
        label="Заголовок"
        codes={codes}
        values={val('title')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />
      <TriTextarea
        label="Текст"
        codes={codes}
        values={val('description')}
        onChange={(code, v) => onUpdate(comp.id, [code, 'description'], v)}
      />
    </div>
  );
}

function ensurePart(c, codes) {
  codes.forEach((code) => {
    if (!c[code]) c[code] = {};
    ['inperson', 'remote'].forEach((side) => {
      if (!c[code][side]) c[code][side] = { title: '', subtitle: '', benefits: [] };
      const b = c[code][side].benefits;
      if (!Array.isArray(b)) c[code][side].benefits = [];
      while (c[code][side].benefits.length < 3) {
        c[code][side].benefits.push({ title: '', text: '' });
      }
    });
  });
}

export function HomeParticipationEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => {
    const m = merge(comp, changes);
    ensurePart(m, codes.length ? codes : ['en', 'ru', 'uz']);
    return m;
  }, [comp, changes, codes]);

  const setFmt = (code, side, field, v) => {
    onUpdate(comp.id, [code, side, field], v);
  };
  const setBen = (code, side, i, field, v) => {
    onUpdate(comp.id, [code, side, 'benefits', String(i), field], v);
  };

  const sides = [
    { key: 'inperson', title: 'Очное участие' },
    { key: 'remote', title: 'Удалённое (только публикация)' },
  ];

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <Users size={22} />
        <div>
          <h3 className="hero-home-editor__title">Форматы участия</h3>
          <p className="hero-home-editor__hint">Заголовок секции и карточки по языкам.</p>
        </div>
      </div>
      <TriInput
        label="Заголовок секции (над двумя колонками)"
        codes={codes}
        values={Object.fromEntries(codes.map((code) => [code, c[code]?.title ?? '']))}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />

      {sides.map(({ key, title }) => (
        <div key={key} style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
          <h4 style={{ marginBottom: '0.75rem' }}>{title}</h4>
          <TriInput
            label="Заголовок карточки"
            codes={codes}
            values={Object.fromEntries(
              codes.map((code) => [code, c[code]?.[key]?.title ?? '']),
            )}
            onChange={(code, v) => setFmt(code, key, 'title', v)}
          />
          <TriInput
            label="Подзаголовок"
            codes={codes}
            values={Object.fromEntries(
              codes.map((code) => [code, c[code]?.[key]?.subtitle ?? '']),
            )}
            onChange={(code, v) => setFmt(code, key, 'subtitle', v)}
          />
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ marginTop: '0.75rem', background: '#f8fafc', padding: '0.75rem', borderRadius: 8 }}>
              <strong style={{ fontSize: '0.85rem' }}>Преимущество {i + 1}</strong>
              <TriInput
                label="Заголовок пункта"
                codes={codes}
                values={Object.fromEntries(
                  codes.map((code) => [
                    code,
                    c[code]?.[key]?.benefits?.[i]?.title ?? '',
                  ]),
                )}
                onChange={(code, v) => setBen(code, key, i, 'title', v)}
              />
              <TriTextarea
                label="Текст"
                codes={codes}
                rows={2}
                values={Object.fromEntries(
                  codes.map((code) => [
                    code,
                    c[code]?.[key]?.benefits?.[i]?.text ?? '',
                  ]),
                )}
                onChange={(code, v) => setBen(code, key, i, 'text', v)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ensureDatesShape(c, codes) {
  codes.forEach((code) => {
    if (!c[code] || typeof c[code] !== 'object') c[code] = {};
    if (!c[code].inperson) c[code].inperson = { title: '', items: [] };
    if (!c[code].remote) c[code].remote = { title: '', items: [] };
    if (!Array.isArray(c[code].inperson.items)) c[code].inperson.items = [];
    if (!Array.isArray(c[code].remote.items)) c[code].remote.items = [];
  });
}

export function HomeDatesEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => {
    const m = merge(comp, changes);
    ensureDatesShape(m, codes.length ? codes : ['en', 'ru', 'uz']);
    return m;
  }, [comp, changes, codes]);

  const sides = [
    { key: 'inperson', title: 'Очное участие' },
    { key: 'remote', title: 'Удалённое участие' },
  ];

  const addItem = (code, side) => {
    const items = Array.isArray(c[code]?.[side]?.items) ? c[code][side].items : [];
    onUpdate(comp.id, [code, side, 'items'], [...items, { label: '', value: '' }]);
  };

  const removeItem = (code, side, idx) => {
    const items = Array.isArray(c[code]?.[side]?.items) ? c[code][side].items : [];
    onUpdate(
      comp.id,
      [code, side, 'items'],
      items.filter((_, i) => i !== idx),
    );
  };

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <CalendarDays size={22} />
        <div>
          <h3 className="hero-home-editor__title">Важные даты</h3>
          <p className="hero-home-editor__hint">Две колонки, карточки редактируются по языкам.</p>
        </div>
      </div>

      {sides.map(({ key, title }) => (
        <div
          key={key}
          style={{
            marginTop: '1.25rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <h4 style={{ marginBottom: '0.75rem' }}>{title}</h4>
          <TriInput
            label="Заголовок колонки"
            codes={codes}
            values={Object.fromEntries(codes.map((code) => [code, c[code]?.[key]?.title ?? '']))}
            onChange={(code, v) => onUpdate(comp.id, [code, key, 'title'], v)}
          />

          {codes.map((code) => {
            const items = Array.isArray(c[code]?.[key]?.items) ? c[code][key].items : [];
            return (
              <div key={code} style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span className="hero-lang-code">{code.toUpperCase()}</span>
                  <button
                    type="button"
                    className="hero-media-clear"
                    onClick={() => addItem(code, key)}
                    style={{ width: 'auto', padding: '0.35rem 0.5rem', borderRadius: 8 }}
                  >
                    <Plus size={14} /> Добавить дату
                  </button>
                </div>
                {items.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Нет карточек</div>
                ) : (
                  items.map((it, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr auto',
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <input
                        type="text"
                        value={it?.label ?? ''}
                        onChange={(e) =>
                          onUpdate(comp.id, [code, key, 'items', String(idx), 'label'], e.target.value)
                        }
                        placeholder="Подпись"
                        style={{ padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 8 }}
                      />
                      <input
                        type="text"
                        value={it?.value ?? ''}
                        onChange={(e) =>
                          onUpdate(comp.id, [code, key, 'items', String(idx), 'value'], e.target.value)
                        }
                        placeholder="Значение (дата/период)"
                        style={{ padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 8 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(code, key, idx)}
                        style={{
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          borderRadius: 8,
                          padding: '0.45rem 0.55rem',
                          cursor: 'pointer',
                        }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ensureTopicsShape(c, codes) {
  const defaultGroups = [
    { title1: '', title2: '', items: [] },
    { title1: '', title2: '', items: [] },
    { title1: '', title2: '', items: [] },
    { title1: '', title2: '', items: [] },
    { title1: '', title2: '', items: [] },
  ];
  codes.forEach((code) => {
    if (!c[code] || typeof c[code] !== 'object') c[code] = {};
    if (typeof c[code].title !== 'string') c[code].title = '';
    if (!Array.isArray(c[code].groups)) c[code].groups = defaultGroups;
    // минимально 5 групп, чтобы совпасть с текущим UI
    while (c[code].groups.length < 5) c[code].groups.push({ title1: '', title2: '', items: [] });
    c[code].groups = c[code].groups.map((g) => ({
      title1: g?.title1 ?? '',
      title2: g?.title2 ?? '',
      items: Array.isArray(g?.items) ? g.items : [],
    }));
  });
}

export function HomeTopicsEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const c = useMemo(() => {
    const m = merge(comp, changes);
    ensureTopicsShape(m, codes.length ? codes : ['en', 'ru', 'uz']);
    return m;
  }, [comp, changes, codes]);

  const addTopicItem = (code, groupIdx) => {
    const items = Array.isArray(c[code]?.groups?.[groupIdx]?.items) ? c[code].groups[groupIdx].items : [];
    onUpdate(comp.id, [code, 'groups', String(groupIdx), 'items'], [...items, '']);
  };

  const removeTopicItem = (code, groupIdx, itemIdx) => {
    const items = Array.isArray(c[code]?.groups?.[groupIdx]?.items) ? c[code].groups[groupIdx].items : [];
    onUpdate(
      comp.id,
      [code, 'groups', String(groupIdx), 'items'],
      items.filter((_, i) => i !== itemIdx),
    );
  };

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <List size={22} />
        <div>
          <h3 className="hero-home-editor__title">Темы</h3>
          <p className="hero-home-editor__hint">Группы: 2 строки заголовка + список пунктов.</p>
        </div>
      </div>

      <TriInput
        label="Заголовок секции (Topics)"
        codes={codes}
        values={Object.fromEntries(codes.map((code) => [code, c[code]?.title ?? '']))}
        onChange={(code, v) => onUpdate(comp.id, [code, 'title'], v)}
      />

      {[0, 1, 2, 3, 4].map((groupIdx) => (
        <div
          key={groupIdx}
          style={{
            marginTop: '1.25rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Группа {groupIdx + 1}
          </h4>

          <TriInput
            label="Заголовок (строка 1)"
            codes={codes}
            values={Object.fromEntries(
              codes.map((code) => [code, c[code]?.groups?.[groupIdx]?.title1 ?? '']),
            )}
            onChange={(code, v) => onUpdate(comp.id, [code, 'groups', String(groupIdx), 'title1'], v)}
          />
          <TriInput
            label="Заголовок (строка 2)"
            codes={codes}
            values={Object.fromEntries(
              codes.map((code) => [code, c[code]?.groups?.[groupIdx]?.title2 ?? '']),
            )}
            onChange={(code, v) => onUpdate(comp.id, [code, 'groups', String(groupIdx), 'title2'], v)}
          />

          {codes.map((code) => {
            const items = Array.isArray(c[code]?.groups?.[groupIdx]?.items)
              ? c[code].groups[groupIdx].items
              : [];
            return (
              <div key={code} style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span className="hero-lang-code">{code.toUpperCase()}</span>
                  <button
                    type="button"
                    className="hero-media-clear"
                    onClick={() => addTopicItem(code, groupIdx)}
                    style={{ width: 'auto', padding: '0.35rem 0.5rem', borderRadius: 8 }}
                  >
                    <Plus size={14} /> Добавить пункт
                  </button>
                </div>
                {items.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Нет пунктов</div>
                ) : (
                  items.map((it, itemIdx) => (
                    <div
                      key={itemIdx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <input
                        type="text"
                        value={it ?? ''}
                        onChange={(e) =>
                          onUpdate(
                            comp.id,
                            [code, 'groups', String(groupIdx), 'items', String(itemIdx)],
                            e.target.value,
                          )
                        }
                        placeholder="Текст пункта"
                        style={{ padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 8 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeTopicItem(code, groupIdx, itemIdx)}
                        style={{
                          border: '1px solid #e2e8f0',
                          background: '#fff',
                          borderRadius: 8,
                          padding: '0.45rem 0.55rem',
                          cursor: 'pointer',
                        }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
